import './index.css'

import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { NavigateFunction, Outlet, useNavigate } from 'react-router-dom'

import {
  CHANNELS_LIMITED,
  CHANNELS_UNLIMITED,
  SHAREABLE_CHANNELS,
} from '@disco/constants/FeatureTypes'
import {
  CUSTOM_CHANNEL,
  MAX_CHANNELS_LIMITED_MODE,
} from '@disco/constants/GroupTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import * as ExplorerActions from '@disco/state-management/actions/ExplorerActions'
import * as GroupsActions from '@disco/state-management/actions/GroupActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  makeGetFeatureData,
  makeIsFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getCustomChannels,
  getSettingsChannels,
  isGroupsExcessLimit,
  makeGetGroupsRestrictions,
} from '@disco/state-management/selectors/groups'

import type { Group } from 'types/group'

import GroupModal from 'components/common/custom-libraries/edit-form'
import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import { CustomLibrariesModal } from 'components/modals/custom-libraries-modal'
import DeleteGroupDialog from 'components/modals/dialogs/delete-group-dialog'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ContactUsLink from 'components/ui/contact-us-link'
import DataTable from 'components/ui/data-table'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import InlineLink from 'components/ui/inline-link'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import PricingBadge from 'components/ui/pricing-badge'

import type { RootState } from 'store/configureStore'

const MODAL_LOCAL_STORAGE_KEY = 'CustomLibraries_BannerDescription'

type OwnProps = {
  navigate: NavigateFunction
}

function mapStateToProps(state: RootState) {
  const getFeatureData = makeGetFeatureData()
  const {
    isFeatureEnabled: channelsUnlimited,
    isTrial,
    badgeTitle,
  } = getFeatureData(state, {
    featureName: CHANNELS_UNLIMITED,
  })
  const isFeatureEnabled = makeIsFeatureEnabled()
  const channelsEnabled = isFeatureEnabled(state, {
    featureName: CHANNELS_LIMITED,
  })
  const isFeatureEnabled3 = makeIsFeatureEnabled()
  const shareableChannelsEnabled = isFeatureEnabled3(state, {
    featureName: SHAREABLE_CHANNELS,
  })
  const visibleChannels = getSettingsChannels(state)
  const getGroupsRestrictions = makeGetGroupsRestrictions()
  return {
    ...state.groups,
    user: state.user,
    channelsLimited: !channelsUnlimited,
    channelsEnabled: channelsEnabled || channelsUnlimited,
    isTrial,
    badgeTitle,
    groupsLoaded: state.groups.isFetched,
    isExcessLimit: isGroupsExcessLimit(state),
    customChannels: getCustomChannels(state),
    visibleChannels,
    restrictions: getGroupsRestrictions(state, {
      ids: visibleChannels.map(g => g.id),
    }),
    isPhone: isDeviceTypePhone(state),
    shareableChannelsEnabled: shareableChannelsEnabled,
  }
}

const mapDispatchToProps = {
  ...GroupsActions,
  ...ExplorerActions,
  sendMixpanelEvent,
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type
type PropsFromRedux = ConnectedProps<typeof connector>

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps

type State = {
  currentGroup: Group | null
  showEditModal: boolean
  showDeleteDialog: boolean
  showUpgradeMoreModal: boolean
}

class ChannelsSettings extends Component<Props, State> {
  state = {
    currentGroup: null,
    showEditModal: false,
    showDeleteDialog: false,
    showUpgradeMoreModal: false,
  }

  componentDidMount() {
    this.props.sendMixpanelEvent('Open Channels tab in settings')
    this.props.loadGroups()
  }

  _toggleConfirmationDialog = (group?: Group) => {
    this.setState({
      showDeleteDialog: !this.state.showDeleteDialog,
      currentGroup: !this.state.showDeleteDialog ? group : null,
    })
  }
  _onDeleteChannel = () => {
    this.props.deleteGroup(this.state.currentGroup)

    this._toggleConfirmationDialog()
  }
  _newGroup = () => {
    const dontShowUpgradeMoreModal = window.localStorage.getItem(
      MODAL_LOCAL_STORAGE_KEY,
    )

    const showEditModal = () => {
      this.setState({
        currentGroup: null,
        showEditModal: true,
      })
    }

    if (
      !this.props.isExcessLimit ||
      (dontShowUpgradeMoreModal && this.props.isTrial)
    ) {
      showEditModal()
      return
    }

    if (this.props.isExcessLimit) {
      this.setState({
        showUpgradeMoreModal: true,
      })
    } else {
      showEditModal()
    }
  }

  _editGroup(group) {
    this.setState({
      currentGroup: group,
      showEditModal: true,
    })
  }

  _onCloseModal = () => {
    this.setState({
      currentGroup: null,
      showEditModal: false,
    })
  }
  _onCloseUpgradeMoreModal = () => {
    this.setState({
      showUpgradeMoreModal: false,
    })
  }
  _onActionClick = (group, action) => {
    switch (action) {
      case 'Delete':
        this._toggleConfirmationDialog(group)

        break

      case 'Edit':
        this._editGroup(group)

        break

      case 'Share':
        const shareRoute =
          this.props.user.business.private_metadata.territories_enabled &&
          this.props.user.territories.length > 1
            ? 'share-warning'
            : 'share'
        this.props.navigate(`/settings/channels/${group.id}/${shareRoute}`)
        break

      default:
        throw new Error('Not implemented')
    }
  }

  renderAlert() {
    const { isTrial } = this.props

    if (this.props.isExcessLimit) {
      return (
        <Alert icon="warning" status="warning">
          {isTrial ? (
            <>
              <InlineLink to="/pricing">Plus and Pro</InlineLink> plans allow
              unlimited channels. Lite plans are limited to{' '}
              {MAX_CHANNELS_LIMITED_MODE}.
            </>
          ) : (
            <>
              Want more than {MAX_CHANNELS_LIMITED_MODE} channels?{' '}
              <ContactUsLink intercomMsg="I’d like to discuss upgrading for more channels.">
                Upgrade
              </ContactUsLink>
            </>
          )}
        </Alert>
      )
    }
  }

  isDowngradeChannel(index) {
    const { channelsLimited } = this.props
    return channelsLimited && index > MAX_CHANNELS_LIMITED_MODE - 1
  }

  changeGroups() {
    const { channelsLimited, visibleChannels, restrictions } = this.props

    if (channelsLimited) {
      visibleChannels.sort((group1, group2) => {
        return group1.created > group2.created ? -1 : 1
      })
    }

    return visibleChannels.map((group, index) => {
      const isDowngradeChannel = this.isDowngradeChannel(index)
      return {
        ...group,
        isDowngradeChannel,
        name: isDowngradeChannel ? (
          <div className="CustomLibraries_DowngradeChannel">{group.name}</div>
        ) : (
          group.name
        ),
        disableActions: restrictions[group.id]?.lock_structure,
      }
    })
  }

  render() {
    const {
      channelsLimited,
      isTrial,
      channelsEnabled,
      badgeTitle,
      groupsLoaded,
      isPhone,
      shareableChannelsEnabled,
    } = this.props
    const dontShowUpgradeMoreModal = window.localStorage.getItem(
      MODAL_LOCAL_STORAGE_KEY,
    )
    if (!channelsEnabled) return null
    const customGroupsFiltered = this.changeGroups()
    const customGroups = (
      <DataTable
        appearance="plain"
        columns={[
          {
            id: 'name',
          },
          {
            contentType: 'button',
            width: 'content',
            render: (group: Record<string, any>) => {
              const ellipsisOptions = [
                {
                  label: 'Share',
                  value: 'Share',
                },
                {
                  label: 'Rename',
                  value: 'Edit',
                  disabled: !group.editable,
                },
                {
                  label: 'Delete',
                  value: 'Delete',
                  destructive: true,
                  disabled: !group.editable || group.disableActions,
                },
              ]
              return (
                <EllipsisMenu
                  disabled={group.isDowngradeChannel}
                  compact
                  icon="ellipsis"
                  options={ellipsisOptions}
                  hasArrow={false}
                  onOptionClick={value => this._onActionClick(group, value)}
                />
              )
            },
          },
        ]}
        data={customGroupsFiltered}
        emptyState="No channels here yet! Create a channel via the + button above."
      />
    )
    const customGroupsModals = customGroupsFiltered.map(group => {
      if (
        this.state.showEditModal &&
        this.state.currentGroup &&
        group.id === this.state.currentGroup.id
      ) {
        return (
          <GroupModal
            {...this.props}
            {...group}
            key={group.id}
            groupType={CUSTOM_CHANNEL}
            onSubmit={this._onCloseModal}
            onClose={this._onCloseModal}
          />
        )
      }
    })
    let newEditForm = null

    if (this.state.showEditModal && !this.state.currentGroup) {
      newEditForm = (
        <GroupModal
          {...this.props}
          groupType={CUSTOM_CHANNEL}
          onSubmit={this._onCloseModal}
          onClose={this._onCloseModal}
        />
      )
    }

    let deleteConfirmationDialog = null

    if (this.state.showDeleteDialog) {
      deleteConfirmationDialog = (
        <DeleteGroupDialog
          onConfirm={this._onDeleteChannel}
          onCancel={this._toggleConfirmationDialog}
          group={this.state.currentGroup}
        />
      )
    }

    return (
      <>
        <DocumentHeadTags title="Settings | Channels | DISCO" />
        <PageHeader
          title={
            <>
              Manage channels
              <HelpTooltip id="tooltip_channels_settings" />
            </>
          }
          actions={
            <Button
              disabled={!groupsLoaded}
              icon="add-circle"
              tooltip={
                this.props.isExcessLimit ? (
                  <div className="CustomLibraries_LimitedTooltip">
                    Want more than {MAX_CHANNELS_LIMITED_MODE} channels?{' '}
                    <PricingBadge title={badgeTitle} />
                  </div>
                ) : !isPhone ? (
                  'New Channel'
                ) : undefined
              }
              onClick={this._newGroup}
            />
          }
        />
        <PageContainer>
          {isTrial && this.renderAlert()}
          {customGroups}
          {!isTrial && this.renderAlert()}
          {customGroupsModals}
          {newEditForm}
          {deleteConfirmationDialog}
          {(channelsLimited || (isTrial && !dontShowUpgradeMoreModal)) && (
            <CustomLibrariesModal
              onCloseUpgradeMoreModal={this._onCloseUpgradeMoreModal}
              showUpgradeMoreModal={this.state.showUpgradeMoreModal}
              isTrial={isTrial}
              onOpenCreateGroupModal={() => {
                this.setState({
                  currentGroup: null,
                  showEditModal: true,
                  showUpgradeMoreModal: false,
                })
              }}
              id={MODAL_LOCAL_STORAGE_KEY}
            />
          )}
          <Outlet />
        </PageContainer>
      </>
    )
  }
}

const ConnectedChannelsSettings = connector(ChannelsSettings)

const ChannelsSettingsWithNavigate = (props: any) => {
  const navigate = useNavigate()
  return <ConnectedChannelsSettings {...props} navigate={navigate} />
}

export default ChannelsSettingsWithNavigate
